<template>
  <div class="login-container">
    <!-- 动态背景装饰 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <!-- 登录区域靠右 -->
    <div class="login-wrapper">
      <div class="login-box">
        <div class="logo-wrapper">
          <!-- <img src="@/assets/logo.png" alt="Logo" class="logo" /> -->
        </div>
        
        <div class="login-header">
          <h2 class="login-title">道上麦车</h2>
          <p class="login-subtitle">智能化试驾管理系统</p>
        </div>
        
        <el-form :model="loginForm" :rules="rules" ref="loginFormRef" class="login-form" @keyup.enter="handleLogin">
          <el-form-item prop="phone">
            <div class="input-wrapper">
              <label class="input-label">手机号</label>
              <el-input
                v-model="loginForm.phone"
                placeholder="请输入手机号"
                size="large"
                maxlength="11"
                clearable
              />
            </div>
          </el-form-item>
          
          <el-form-item prop="password">
            <div class="input-wrapper">
              <label class="input-label">密码</label>
              <el-input
                v-model="loginForm.password"
                type="password"
                placeholder="请输入密码"
                size="large"
                show-password
              />
            </div>
          </el-form-item>

          <div class="form-options">
            <el-checkbox v-model="rememberMe" @change="handleRemember">记住我</el-checkbox>
            <a href="#" class="forgot-link" @click.prevent="handleForgotPassword">忘记密码？</a>
          </div>

          <el-form-item>
            <el-button 
              type="primary" 
              size="large" 
              class="login-btn" 
              @click="handleLogin"
              :loading="loading"
            >
              {{ loading ? '登录中...' : '登录' }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginPage'
}
</script>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { login } from '@/api/auth'
import { setToken } from '@/utils/request'

const router = useRouter()
const loginFormRef = ref(null)
const loading = ref(false)
const rememberMe = ref(false)

// 记住账密的本地存储键名
const REMEMBER_KEY = 'rememberedAccount'

const loginForm = reactive({
  phone: '',
  password: ''
})

// 进入页面时，若之前勾选过“记住我”，回填账密
onMounted(() => {
  try {
    const saved = localStorage.getItem(REMEMBER_KEY)
    if (saved) {
      const { phone, password } = JSON.parse(saved)
      loginForm.phone = phone || ''
      // 简单 base64 解码（仅防肉眼直读，非加密）
      loginForm.password = password ? decodeURIComponent(escape(atob(password))) : ''
      rememberMe.value = true
    }
  } catch (e) {
    localStorage.removeItem(REMEMBER_KEY)
  }
})

// 根据“记住我”勾选状态保存或清除账密
const handleRemember = () => {
  if (rememberMe.value) {
    localStorage.setItem(
      REMEMBER_KEY,
      JSON.stringify({
        phone: loginForm.phone,
        password: btoa(unescape(encodeURIComponent(loginForm.password)))
      })
    )
  } else {
    localStorage.removeItem(REMEMBER_KEY)
  }
}

const rules = {
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ]
}

const handleLogin = () => {
  loginFormRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const res = await login({
          phone: loginForm.phone,
          password: loginForm.password
        })
        // 存储 token，后续请求拦截器自动携带 Authorization: Bearer
        setToken(res.data.token)
        // 存储用户信息
        localStorage.setItem('userInfo', JSON.stringify(res.data))
        // 根据“记住我”保存或清除账密
        handleRemember()
        ElMessage.success('登录成功')
        router.push('/home')
      } catch (error) {
        // 错误提示已在请求拦截器中统一处理
        console.error('登录失败：', error)
      } finally {
        loading.value = false
      }
    } else {
      // 验证失败时的提示
      if (!loginForm.phone) {
        ElMessage.warning('请输入手机号')
      } else if (!/^1[3-9]\d{9}$/.test(loginForm.phone)) {
        ElMessage.warning('请输入正确的手机号')
      } else if (!loginForm.password) {
        ElMessage.warning('请输入密码')
      } else if (loginForm.password.length < 6) {
        ElMessage.warning('密码长度不能少于6位')
      }
      return false
    }
  })
}

const handleForgotPassword = () => {
  router.push('/updatePwd')
}
</script>

<style scoped>
.login-container {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%);
}

/* 动态背景装饰 */
.bg-decoration {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 20s infinite ease-in-out;
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  left: -100px;
  animation-delay: 0s;
}

.circle-2 {
  width: 400px;
  height: 400px;
  bottom: -150px;
  right: -150px;
  animation-delay: 5s;
}

.circle-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 30%;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

/* 登录区域靠右 */
.login-wrapper {
  position: relative;
  z-index: 1;
  margin-left: auto;
  margin-right: 10%;
  animation: fadeInUp 0.8s ease-out;
}

.login-box {
  width: min(440px, 90vw);
  padding: min(45px, 4vh) min(40px, 5vw);
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.logo-wrapper {
  text-align: center;
  margin-bottom: min(20px, 2vh);
  animation: fadeInDown 1s ease-out;
}

.logo {
  width: min(70px, 10vw);
  height: min(70px, 10vw);
  object-fit: contain;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.15));
}

.login-header {
  text-align: center;
  margin-bottom: min(35px, 3vh);
}

.login-title {
  font-size: clamp(20px, 2.5vw, 24px);
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.login-subtitle {
  font-size: clamp(12px, 1.5vw, 14px);
  color: #666;
  font-weight: 400;
}

.login-form {
  margin-top: min(25px, 2.5vh);
}

.input-wrapper {
  margin-bottom: 4px;
  width: 100%;
}

.input-label {
  display: block;
  font-size: clamp(12px, 1.5vw, 14px);
  font-weight: 500;
  color: #333;
  margin-bottom: min(8px, 1vh);
}

:deep(.el-form-item) {
  margin-bottom: min(20px, 2vh);
}

:deep(.el-form-item__content) {
  width: 100%;
}

:deep(.el-input) {
  width: 100%;
  display: block;
}

:deep(.el-input__wrapper) {
  width: 100%;
  display: flex;
  padding: min(12px, 1.2vh) min(16px, 1.5vw);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.25);
}

:deep(.el-input__inner) {
  font-size: clamp(13px, 1.5vw, 15px);
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: min(20px, 2vh) 0 min(30px, 3vh);
}

:deep(.el-checkbox__label) {
  font-size: clamp(12px, 1.5vw, 14px);
}

.forgot-link {
  color: #409eff;
  text-decoration: none;
  font-size: clamp(12px, 1.5vw, 14px);
  font-weight: 500;
  transition: color 0.3s;
}

.forgot-link:hover {
  color: #66b1ff;
}

.login-btn {
  width: 100%;
  height: min(48px, 5vh);
  font-size: clamp(14px, 1.8vw, 16px);
  font-weight: 600;
  border-radius: 12px;
  background: #409eff;
  border: none;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.3);
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(64, 158, 255, 0.4);
  background: #66b1ff;
}

.login-btn:active {
  transform: translateY(0);
}

/* 动画 */
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .login-wrapper {
    margin-left: auto;
    margin-right: auto;
  }
}

@media (max-width: 480px) {
  .login-box {
    width: 90%;
    padding: 40px 30px;
  }
  
  .login-title {
    font-size: 28px;
  }
  
  .logo {
    width: 80px;
    height: 80px;
  }
  
  .login-wrapper {
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
